/**
 * Serviço BFF (Backend for Frontend) para Recommendation Engine
 * Implementa o padrão de desacoplamento: Frontend → Data-Provider → Frontend → Recommendation-Engine
 */

interface ProviderData {
  status: 'SUCCESS' | 'ERROR' | 'TIMEOUT';
  data?: any;
  error_message?: string;
}

interface AnalysisRequest {
  social_id: string;
  document_type: 'CPF' | 'CNPJ';
  business_area: 'PLD' | 'CREDIT' | 'COMPLIANCE';
  provider_data: Record<string, ProviderData>;
  additional_data?: Record<string, any>;
}

interface AnalysisResponse {
  analysis_id: string;
  social_id: string;
  recommendation: 'APROVADO' | 'REPROVADO' | 'PENDENTE';
  risk_score: number;
  analysis_date: string;
  rules_applied: string[];
  message: string;
}

class RecommendationBFFService {
  private readonly API_KEY: string;
  private readonly DATA_PROVIDER_BASE_URL: string;
  private readonly RECOMMENDATION_ENGINE_BASE_URL: string;

  constructor() {
    this.API_KEY = process.env.REACT_APP_API_KEY || '';
    this.DATA_PROVIDER_BASE_URL = process.env.REACT_APP_DATA_PROVIDER_API_URL || '';
    this.RECOMMENDATION_ENGINE_BASE_URL = process.env.REACT_APP_RECOMMENDATION_ENGINE_API_URL || '';
  }

  /**
   * Fluxo completo BFF: busca dados + análise de recomendação
   */
  async analyzeDocument(socialId: string, documentType: 'CPF' | 'CNPJ', businessArea: 'PLD' | 'CREDIT' | 'COMPLIANCE' = 'PLD'): Promise<AnalysisResponse> {
    try {
      console.log(`🔄 Iniciando análise BFF para ${socialId} (${documentType})`);
      
      // 1. Buscar dados de todos os providers
      const providerData = await this.fetchAllProviderData(socialId, documentType);
      
      // 2. Enviar dados consolidados para recommendation-engine
      const analysis = await this.sendToRecommendationEngine({
        social_id: socialId,
        document_type: documentType,
        business_area: businessArea,
        provider_data: providerData
      });
      
      console.log(`✅ Análise BFF concluída: ${analysis.recommendation}`);
      return analysis;
      
    } catch (error) {
      console.error('❌ Erro na análise BFF:', error);
      throw error;
    }
  }

  /**
   * Busca dados de todos os providers disponíveis
   */
  private async fetchAllProviderData(socialId: string, documentType: 'CPF' | 'CNPJ'): Promise<Record<string, ProviderData>> {
    const providers = ['INOVAMIND', 'ESCAVADOR', 'QUOD', 'QUOD-PJ'];
    const providerData: Record<string, ProviderData> = {};
    
    // Buscar dados de cada provider em paralelo
    const promises = providers.map(async (provider) => {
      try {
        console.log(`📡 Buscando dados do ${provider}...`);
        const data = await this.fetchProviderData(socialId, documentType, provider);
        
        providerData[provider] = {
          status: 'SUCCESS',
          data: data
        };
        
        console.log(`✅ ${provider}: dados obtidos com sucesso`);
        
      } catch (error) {
        console.warn(`⚠️ ${provider}: erro ao buscar dados`, error);
        
        providerData[provider] = {
          status: 'ERROR',
          error_message: error instanceof Error ? error.message : 'Erro desconhecido'
        };
      }
    });
    
    await Promise.allSettled(promises);
    
    const successCount = Object.values(providerData).filter(p => p.status === 'SUCCESS').length;
    console.log(`📊 Dados coletados: ${successCount}/${providers.length} providers com sucesso`);
    
    return providerData;
  }

  /**
   * Busca dados de um provider específico
   */
  private async fetchProviderData(socialId: string, documentType: 'CPF' | 'CNPJ', provider: string): Promise<any> {
    const endpoint = this.getProviderEndpoint(provider, documentType);
    const url = `${this.DATA_PROVIDER_BASE_URL}${endpoint}`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'x-api-key': this.API_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        document: socialId,
        document_type: documentType
      })
    });
    
    if (!response.ok) {
      throw new Error(`${provider} API error: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Mapeia provider para endpoint correto
   */
  private getProviderEndpoint(provider: string, documentType: 'CPF' | 'CNPJ'): string {
    const endpoints: Record<string, string> = {
      'INOVAMIND': '/inovamind/analyze',
      'ESCAVADOR': '/escavador/analyze',
      'QUOD': documentType === 'CPF' ? '/quod/analyze' : '/quod-pj/analyze',
      'QUOD-PJ': '/quod-pj/analyze'
    };
    
    return endpoints[provider] || `/providers/${provider.toLowerCase()}/analyze`;
  }

  /**
   * Envia dados consolidados para recommendation-engine
   */
  private async sendToRecommendationEngine(request: AnalysisRequest): Promise<AnalysisResponse> {
    const url = `${this.RECOMMENDATION_ENGINE_BASE_URL}/recommendations/analyze-with-data`;
    
    console.log(`🎯 Enviando para recommendation-engine:`, {
      social_id: request.social_id,
      document_type: request.document_type,
      business_area: request.business_area,
      providers_count: Object.keys(request.provider_data).length
    });
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'x-api-key': this.API_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(request)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Recommendation Engine error: ${response.status} ${response.statusText} - ${errorText}`);
    }
    
    return await response.json();
  }

  /**
   * Análise de lote completo (para compatibilidade com telas existentes)
   */
  async analyzeBatch(batchId: string): Promise<any> {
    try {
      console.log(`🔄 Iniciando análise BFF do lote ${batchId}`);
      
      // 1. Buscar dados do lote no data-provider
      const batchData = await this.fetchBatchData(batchId);
      
      // 2. Analisar cada documento do lote
      if (batchData.reports && Array.isArray(batchData.reports)) {
        const analysisPromises = batchData.reports.map(async (report: any) => {
          try {
            const analysis = await this.analyzeDocument(
              report.document || report.social_id,
              report.document_type || 'CPF',
              'PLD'
            );
            
            // Enriquecer report com dados da análise
            report.backend_recommendation = analysis.recommendation;
            report.backend_reasons = analysis.rules_applied;
            report.backend_risk_score = analysis.risk_score;
            report.backend_analysis_id = analysis.analysis_id;
            
            return analysis;
            
          } catch (error) {
            console.error(`Erro na análise do documento ${report.document}:`, error);
            
            // Fallback para aprovação padrão em caso de erro
            report.backend_recommendation = 'APROVADO';
            report.backend_reasons = ['Erro na análise - aprovação padrão'];
            report.backend_risk_score = 0.5;
            
            return null;
          }
        });
        
        await Promise.allSettled(analysisPromises);
      }
      
      console.log(`✅ Análise BFF do lote concluída`);
      return batchData;
      
    } catch (error) {
      console.error('❌ Erro na análise BFF do lote:', error);
      throw error;
    }
  }

  /**
   * Busca dados do lote no data-provider
   */
  private async fetchBatchData(batchId: string): Promise<any> {
    const url = `${this.DATA_PROVIDER_BASE_URL}/batch-reports/${batchId}/page-view`;
    
    const response = await fetch(url, {
      headers: {
        'x-api-key': this.API_KEY,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Batch data error: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }
}

// Instância singleton
export const recommendationBFFService = new RecommendationBFFService();

// Export da classe para testes
export { RecommendationBFFService };
